import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as usuariosApi from '../../api/usuarios';
import { useAuth } from '../../contexts/AuthContext';
import { useEmpresaSelecionada } from '../../contexts/EmpresaContext';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import SearchableSelect from '../../components/common/SearchableSelect';
import { usePaginacao } from '../../hooks/usePaginacao';
import { useSortable } from '../../hooks/useSortable';
import { getErrorMessage } from '../../utils/apiError';

export default function Usuarios() {
  const { user } = useAuth();
  const isSuperAdmin = user?.perfil === 'super_admin';
  const { empresas } = useEmpresaSelecionada();

  // pode divergir da empresa selecionada no Navbar — aqui é só pra filtrar esta listagem
  const [idEmpresa, setIdEmpresa] = useState('');
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (isSuperAdmin && !idEmpresa) {
      setItens([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    usuariosApi
      .listUsuarios(isSuperAdmin ? idEmpresa : undefined)
      .then((r) => setItens(r.data))
      .catch(() => setErro('Erro ao carregar usuários.'))
      .finally(() => setLoading(false));
  }, [idEmpresa, isSuperAdmin]);

  const filtrados = itens.filter((i) => i.nome.toLowerCase().includes(busca.toLowerCase()));
  const { sorted, Th } = useSortable(filtrados, 'nome');
  const { paginados, Paginacao } = usePaginacao(sorted);

  const alternarAtivo = async (item) => {
    const acao = item.ativo ? 'Desativar' : 'Reativar';
    if (!window.confirm(`${acao} o acesso de "${item.nome}"?`)) return;
    setErro('');
    try {
      await usuariosApi.updateUsuario(item.id_usuario_portal, { ativo: !item.ativo });
      setItens((atual) => atual.map((i) => (i.id_usuario_portal === item.id_usuario_portal ? { ...i, ativo: !item.ativo } : i)));
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao alterar o status do usuário.'));
    }
  };

  return (
    <div>
      <PageHeader titulo="Usuários" acaoLabel="Novo usuário" acaoTo="/usuarios/novo" />

      {erro && (
        <div className="sf-login-alert mb-3">
          <i className="bi bi-exclamation-circle" />
          {erro}
        </div>
      )}

      {isSuperAdmin && (
        <div className="sf-field-label mb-3" style={{ maxWidth: 360 }}>
          <span>Empresa</span>
          <SearchableSelect
            options={empresas.map((e) => ({ value: e.id_empresa, label: e.nome }))}
            value={idEmpresa}
            onChange={setIdEmpresa}
            placeholder="Selecione a empresa..."
          />
        </div>
      )}

      {isSuperAdmin && !idEmpresa ? (
        <EmptyState icon="bi-building" titulo="Selecione uma empresa" texto="Escolha uma empresa acima para ver os usuários dela." />
      ) : (
        <div className="sf-table-card">
          <div className="sf-table-toolbar">
            <div className="sf-search">
              <i className="bi bi-search" />
              <input placeholder="Buscar por nome..." value={busca} onChange={(e) => setBusca(e.target.value)} />
            </div>
          </div>

          {loading ? (
            <div className="p-4">
              <Skeleton linhas={5} />
            </div>
          ) : paginados.length === 0 ? (
            <EmptyState icon="bi-person-badge" titulo="Nenhum usuário encontrado" texto="Ajuste a busca ou cadastre um novo usuário." acaoLabel="Novo usuário" acaoTo="/usuarios/novo" />
          ) : (
            <>
              <div className="table-responsive">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <Th campo="nome">Nome</Th>
                      <th>E-mail</th>
                      <th>Perfil</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {paginados.map((u) => (
                      <tr key={u.id_usuario_portal}>
                        <td>
                          <span className="d-flex align-items-center gap-2">
                            <span className="sf-cell-icon"><i className="bi bi-person" /></span>
                            <span className="sf-cell-title">{u.nome}</span>
                          </span>
                        </td>
                        <td>{u.email}</td>
                        <td><StatusBadge status={u.perfil} /></td>
                        <td><StatusBadge status={u.ativo ? 'ativo' : 'inativo'} /></td>
                        <td className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <Link to={`/usuarios/${u.id_usuario_portal}/editar`} className="sf-btn sf-btn-ghost" title="Editar">
                              <i className="bi bi-pencil" />
                            </Link>
                            <button
                              type="button"
                              className="sf-btn sf-btn-ghost"
                              style={u.ativo ? { color: 'var(--sf-danger-fg)', borderColor: '#f0cfcc' } : undefined}
                              onClick={() => alternarAtivo(u)}
                              title={u.ativo ? 'Desativar' : 'Reativar'}
                            >
                              <i className={`bi ${u.ativo ? 'bi-slash-circle' : 'bi-check-circle'}`} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginacao />
            </>
          )}
        </div>
      )}
    </div>
  );
}
